import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Archive, Captions, Cpu, FileText, MoreHorizontal, Pause, Pencil, Play, RotateCcw, Square, Trash2, UploadCloud, Wand2, X } from 'lucide-react';
import { useT } from '../lib/i18n';
import { useMediaList, useOverview } from '../lib/live';
import { api } from '../lib/api';
import { bytes } from '../lib/format';
import { toast, useIsHost, usePerms, useSession } from '../lib/session';
import { useUploads } from '../features/upload/uploads';
import { SpeedTest } from '../features/speed/SpeedTest';
import { gsap, useEnter } from '../motion/gsap';
import { Modal, ModalTitle, Segmented } from '../ui';
import { MediaTable, StorageCard } from './parts';
import { SubtitlesDialog } from './SubtitlesDialog';
import { EncodeDialog } from './EncodeDialog';
import { TapTwiceButton } from '../ui/tapTwice';
import { stableKeys } from '../lib/keys';
import { TrashCard } from '../features/trash/TrashCard';
import { watchTogether } from '../features/room/session';
import type { Media } from '../lib/types';

export function DrivePage() {
  const ref = useRef<HTMLDivElement>(null);
  const ov = useOverview((s) => s.data);
  const items = useMediaList((s) => s.items);
  const perms = usePerms();
  const [log, setLog] = useState<{ title: string; text: string } | null>(null);
  const [subsFor, setSubsFor] = useState<string | null>(null);
  const subsMedia = items.find((m) => m.id === subsFor) ?? null;
  const [encFor, setEncFor] = useState<string | null>(null);
  const encMedia = items.find((m) => m.id === encFor) ?? null;
  useEnter(ref, []);

  return (
    <div className="grid-main" ref={ref}>
      <div className="stack">
        {perms?.upload && <UploadCard />}
        <LibraryCard onLog={setLog} onSubs={setSubsFor} onEncode={setEncFor} />
        {perms?.trash && <TrashCard />}
      </div>
      <div className="stack">
        {ov && <StorageCard ov={ov} />}
        <EncoderCard encoder={ov?.drive.encoder} />
        <SpeedTest />
      </div>
      <Modal wide open={!!log} onClose={() => setLog(null)}>
        <ModalTitle>{log?.title}</ModalTitle>
        <p className="muted" style={{ fontSize: 12.5, margin: '0 0 12px' }}>
          Processing log (ffmpeg commands, timings and errors)
        </p>
        <div className="joblog">{log?.text ? <JobLog text={log.text} /> : <div className="jl-row"><span /><span>No log yet.</span></div>}</div>
      </Modal>
      <Modal open={!!subsMedia} onClose={() => setSubsFor(null)}>
        {subsMedia && <SubtitlesDialog m={subsMedia} />}
      </Modal>
      <Modal open={!!encMedia} onClose={() => setEncFor(null)}>
        {encMedia && <EncodeDialog m={encMedia} />}
      </Modal>
    </div>
  );
}

/** Which encoder this server has, and what it does with a new film. */
function EncoderCard({ encoder }: { encoder?: string }) {
  const t = useT();
  const hw = /videotoolbox|nvenc|qsv|vaapi|gpu/i.test(encoder ?? '');
  return (
        <section className="card card-dark" data-anim>
          <div className="insight-head">
            <Cpu size={15} className="accent-ic" />
            {t('drive.hw')}
          </div>
          <h3 className="insight-title">{encoder ?? '—'}</h3>
          {hw ? (
            <>
              <ul className="insight-list">
                <li>
                  <span className="dot dot-accent" /> Original remux <span className="mono">bit-exact, HDR too</span>
                </li>
                <li>
                  <span className="dot dot-accent" /> H.264 ladder <span className="mono">4K·1080·720·480, one pass</span>
                </li>
                <li>
                  <span className="dot dot-accent" /> HDR → SDR <span className="mono">tone-mapped on the GPU</span>
                </li>
                <li>
                  <span className="dot dot-accent" /> Self-healing <span className="mono">GPU → CPU, 3 retries</span>
                </li>
              </ul>
              <p className="insight-note">The original plays within seconds. Nothing else is encoded unless you pick it: ⋯ → Encoding &amp; audio on a film.</p>
            </>
          ) : (
            <>
              <ul className="insight-list">
                <li>
                  <span className="dot dot-accent" /> Original remux <span className="mono">plays at once, HDR too</span>
                </li>
                <li>
                  <span className="dot dot-accent" /> Extra versions <span className="mono">only the ones you pick</span>
                </li>
                <li>
                  <span className="dot dot-accent" /> Subtitles <span className="mono">SRT·ASS·VTT → WebVTT</span>
                </li>
                <li>
                  <span className="dot dot-accent" /> Self-healing <span className="mono">3 retries</span>
                </li>
              </ul>
              <p className="insight-note">The original plays within seconds of being added. Nothing else is encoded unless you pick it (⋯ → Encoding &amp; audio on a film); each extra version takes about as long as the film runs.</p>
            </>
          )}
        </section>
  );
}

/** Add films: drop zone, the watched folder, and uploads in progress. */
function UploadCard() {
  const t = useT();
  const ov = useOverview((s) => s.data);
  return (
          <section className="card" data-anim>
            <div className="card-head">
              <div style={{ flex: 1 }}>
                <h2 className="card-title">{t('drive.title')}</h2>
                <div className="card-sub">{t('drive.sub', { label: ov?.drive.name ?? '' })}</div>
              </div>
            </div>
            <Dropzone />
            {ov && (
              <p className="muted" style={{ fontSize: 12, margin: '10px 2px 0' }}>
                {t('drive.folderHint', { path: ov.drive.watchFolder })}
              </p>
            )}
            <UploadList />
          </section>
  );
}

type LibraryFilter = 'all' | 'ready' | 'processing';
const matches = (m: Media, q: string, filter: LibraryFilter) => {
  if (q && !`${m.title} ${m.fileName} ${m.meta?.year ?? ''}`.toLowerCase().includes(q)) return false;
  if (filter === 'ready') return m.playable;
  if (filter === 'processing') return m.status !== 'ready' && m.status !== 'failed';
  return true;
};

/** Every title, searchable (from the header search) and filterable. */
function LibraryCard({ onLog, onSubs, onEncode }: { onLog: (l: { title: string; text: string }) => void; onSubs: (id: string) => void; onEncode: (id: string) => void }) {
  const t = useT();
  const nav = useNavigate();
  const items = useMediaList((s) => s.items);
  const [params] = useSearchParams();
  const q = (params.get('q') ?? '').toLowerCase();
  const [filter, setFilter] = useState<LibraryFilter>('all');
  const rows = items.filter((m) => matches(m, q, filter));
  return (
        <section className="card" data-anim>
          <div className="card-head">
            <div style={{ flex: 1 }}>
              <h2 className="card-title">{t('ov.recent')}</h2>
              <div className="card-sub">{q ? `“${q}” · ${rows.length}` : `${items.length} titles · ${bytes(items.reduce((a, m) => a + m.size, 0))}`}</div>
            </div>
            <Segmented
              value={filter}
              onChange={setFilter}
              options={[
                { value: 'all', label: t('ov.all') },
                { value: 'ready', label: t('pipe.ready') },
                { value: 'processing', label: t('pipe.encoding') },
              ]}
            />
          </div>
          <MediaTable rows={rows} onOpen={(m) => m.playable && watchTogether(m, nav)} actions={(m) => <RowActions m={m} onLog={onLog} onSubs={onSubs} onEncode={onEncode} />} />
        </section>
  );
}

function RowActions({
  m,
  onLog,
  onSubs,
  onEncode,
}: {
  m: Media;
  onLog: (l: { title: string; text: string }) => void;
  onSubs: (id: string) => void;
  onEncode: (id: string) => void;
}) {
  const t = useT();
  const nav = useNavigate();
  const isHost = useIsHost();
  const me = useSession((s) => s.user);
  const refresh = useMediaList((s) => s.refresh);
  const [open, setOpen] = useState(false);
  const menu = useRef<HTMLDivElement>(null);
  const moreBtn = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<{ top?: number; bottom?: number; right: number } | null>(null);
  const canEdit = isHost || m.uploadedBy === me?.id;
  // The menu is portalled to <body> so the table card's overflow can never clip it; it opens upward near the bottom.
  // Measured on the click that opens it, so the menu renders in place without a second pass.
  const toggleMenu = () => {
    if (open || !moreBtn.current) return setOpen(false);
    const r = moreBtn.current.getBoundingClientRect();
    const right = window.innerWidth - r.right;
    setPos(window.innerHeight - r.bottom < 330 ? { bottom: window.innerHeight - r.top + 6, right } : { top: r.bottom + 6, right });
    setOpen(true);
  };
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);
  useEffect(() => {
    if (!open || !pos) return;
    gsap.from(menu.current, { y: pos.bottom !== undefined ? 8 : -8, opacity: 0, scale: 0.96, transformOrigin: pos.bottom !== undefined ? 'bottom right' : 'top right', duration: 0.22, ease: 'power3.out' });
  }, [open, pos]);
  useEffect(() => {
    if (!open) return;
    // The click that opened the menu is still bubbling to window when this effect runs; ignore
    // events that started before it instead of deferring the listener with a timer.
    const openedAt = performance.now();
    const close = (e: MouseEvent) => e.timeStamp > openedAt && !menu.current?.contains(e.target as Node) && setOpen(false);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [open]);
  const act = async (fn: () => Promise<unknown>, ok?: string) => {
    setOpen(false);
    try {
      await fn();
      if (ok) toast(ok, 'accent');
      refresh();
    } catch (e) {
      toast((e as Error).message, 'red');
    }
  };
  return (
    <div className="row" style={{ gap: 6, justifyContent: 'flex-end', position: 'relative' }}>
      {m.playable && (
        <button className="btn btn-black btn-sm" onClick={() => watchTogether(m, nav)}>
          <Play size={13} /> {t('btn.watch')}
        </button>
      )}
      {canEdit && m.encoding && m.encodePlan?.pending && (
        <TapTwiceButton className="btn btn-ghost btn-sm" title={m.stage} armedText="Tap again to stop" onConfirm={() => act(() => api.stopEncode(m.id), 'Encoding stopped: what finished is kept')}>
          <Square size={12} /> Stop
        </TapTwiceButton>
      )}
      {m.status === 'failed' && (
        <button className="btn btn-ghost btn-sm" title={m.error} onClick={() => act(() => api.reprocess(m.id), 'Trying again')}>
          <RotateCcw size={13} /> {t('btn.retry')}
        </button>
      )}
      <button ref={moreBtn} className="btn btn-ghost btn-sm btn-icon" aria-label="More" onClick={toggleMenu}>
        <MoreHorizontal size={15} />
      </button>
      {open &&
        pos &&
        createPortal(
          <div className="menu menu-fixed" ref={menu} style={pos}>
            <RowMenuItems m={m} canEdit={canEdit} isHost={isHost} close={() => setOpen(false)} act={act} onLog={onLog} onSubs={onSubs} onEncode={onEncode} />
          </div>,
          document.body,
        )}
    </div>
  );
}

/** The ⋯ menu of a library row. */
function RowMenuItems({
  m,
  canEdit,
  isHost,
  close,
  act,
  onLog,
  onSubs,
  onEncode,
}: {
  m: Media;
  canEdit: boolean;
  isHost: boolean;
  close: () => void;
  act: (fn: () => Promise<unknown>, ok?: string) => void;
  onLog: (l: { title: string; text: string }) => void;
  onSubs: (id: string) => void;
  onEncode: (id: string) => void;
}) {
  const t = useT();
  return (
    <>
          {canEdit && (
            <button
              onClick={() => {
                const title = prompt('Title', m.title);
                if (title !== null) act(() => api.renameMedia(m.id, title));
              }}
            >
              <Pencil size={14} /> Rename
            </button>
          )}
          <button
            onClick={() => {
              close();
              onSubs(m.id);
            }}
          >
            <Captions size={14} /> Subtitles…
          </button>
          {canEdit && m.status === 'ready' && (
            <button
              onClick={() => {
                close();
                onEncode(m.id);
              }}
            >
              <Wand2 size={14} /> Encoding &amp; audio…
            </button>
          )}
          {canEdit && !m.originalRemoved && !m.removeOriginal && m.renditions?.some((r) => r.copy) && (
            <button
              onClick={() => {
                const msg =
                  `Remove the original file of “${m.title}” to free ${bytes(m.size, 1)}?\n\n` +
                  'It keeps playing in full quality from its own copy, but it can no longer be re-encoded. ' +
                  'The file goes to the Trash (Drive → Trash), where it can be restored for 30 days.' +
                  (m.status !== 'ready' ? '\n\nIt is still encoding: the original is removed as soon as that finishes.' : '');
                if (!confirm(msg)) return close();
                act(async () => {
                  const r = await api.removeOriginal(m.id);
                  toast(r.removedNow ? `Original moved to the Trash (below). Empty it to free ${bytes(m.size, 1)}` : 'The original will move to the Trash when encoding finishes', 'accent');
                });
              }}
            >
              <Archive size={14} /> Remove original ({bytes(m.size, 1)})
            </button>
          )}
          {isHost && !m.originalRemoved && (
            <button onClick={() => act(() => api.reprocess(m.id), 'Re-encoding started')}>
              <RotateCcw size={14} /> Re-encode
            </button>
          )}
          {isHost && (
            <button
              onClick={() => {
                close();
                api
                  .jobLog(m.id)
                  .then((text) => onLog({ title: m.title, text }))
                  .catch((e) => toast((e as Error).message, 'red'));
              }}
            >
              <FileText size={14} /> Processing log
            </button>
          )}
          {canEdit && (
            <button
              className="danger"
              onClick={() => {
                if (confirm(`Move “${m.title}” to the Trash?\n\nIt disappears from the library. You can restore it from Drive → Trash for 30 days; empty the Trash to free its space.`))
                  act(() => api.deleteMedia(m.id), 'Moved to the Trash (Drive → Trash)');
                else close();
              }}
            >
              <Trash2 size={14} /> {t('btn.delete')}
            </button>
          )}
    </>
  );
}

function Dropzone() {
  const t = useT();
  const add = useUploads((s) => s.add);
  const input = useRef<HTMLInputElement>(null);
  const icon = useRef<HTMLDivElement>(null);
  const [over, setOver] = useState(false);
  const enter = () => {
    if (!over) gsap.fromTo(icon.current, { y: 0 }, { y: -8, duration: 0.35, yoyo: true, repeat: 1, ease: 'power2.out' });
    setOver(true);
  };
  return (
    <div
      className={`dropzone${over ? ' over' : ''}`}
      onClick={() => input.current?.click()}
      onDragOver={(e) => {
        e.preventDefault();
        enter();
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        if (e.dataTransfer.files.length) add(e.dataTransfer.files);
      }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && input.current?.click()}
    >
      <div className="dropzone-icon" ref={icon}>
        <UploadCloud size={22} />
      </div>
      <h3>{t('drive.drop')}</h3>
      <p>Resumable uploads, no size cap beyond the host's storage limit. MP4, MOV, MKV, WebM…</p>
      <input
        ref={input}
        type="file"
        accept="video/*,.mkv,.mts,.m2ts"
        multiple
        hidden
        onChange={(e) => {
          if (e.target.files?.length) add(e.target.files);
          e.target.value = '';
        }}
      />
    </div>
  );
}

function UploadList() {
  const t = useT();
  const { uploads, pause, resume, remove } = useUploads();
  if (!uploads.length) return null;
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 2 }}>{t('drive.uploads')}</div>
      {uploads.map((u) => {
        const total = u.body?.size ?? u.file.size; // a fast-started MP4 can differ by a few bytes
        const pct = total ? u.offset / total : 0;
        const eta = u.speed > 0 ? (total - u.offset) / u.speed : 0;
        return (
          <div className="upload-row" key={u.id}>
            <div style={{ minWidth: 0 }}>
              <div className="cell-main" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {u.file.name}
              </div>
              <div className="cell-sub">
                {bytes(u.offset)} / {bytes(total)}
                {u.status === 'uploading' && u.speed > 0 && ` · ${bytes(u.speed)}/s · ${Math.ceil(eta / 60)} min left`}
                {u.status === 'uploading' && u.lane === 'local' && ' · direct to this Mac'}
                {u.status === 'uploading' && u.lane === 'internet' && ' · over the internet'}
                {u.status === 'uploading' && u.lane === 'internet' && eta > 20 * 60 && (
                  <span className="upload-tip">
                    This goes through your internet connection's upload speed. On the host's Mac, open{' '}
                    <span className="mono">http://localhost:8080</span> and add it there: it goes straight to the drive in about a minute.
                  </span>
                )}
                {u.status === 'paused' && ` · ${t('drive.paused')}`}
                {u.status === 'done' && ' · ✓ processing on the host'}
                {u.error && ` · ${u.error}`}
              </div>
            </div>
            <div className="row" style={{ gap: 4 }}>
              {u.status === 'uploading' && (
                <button className="icon-btn" onClick={() => pause(u.id)} aria-label={t('drive.pause')}>
                  <Pause size={15} />
                </button>
              )}
              {(u.status === 'paused' || u.status === 'error') && (
                <button className="icon-btn" onClick={() => resume(u.id)} aria-label={t('drive.resume')}>
                  <Play size={15} />
                </button>
              )}
              <button className="icon-btn" onClick={() => remove(u.id)} aria-label={t('btn.cancel')}>
                <X size={15} />
              </button>
            </div>
            <div className={`progress${u.status === 'done' ? ' accent' : ''}`} style={{ gridColumn: '1 / -1' }}>
              <div style={{ width: `${pct * 100}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

const LOG_LINE = /^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2})\s+(.*)$/;

// Splits "ffmpeg -a x -b y" into one line per option so long commands stay readable.
function splitCommand(cmd: string) {
  return cmd.split(/ (?=-[a-zA-Z])/);
}

function JobLog({ text }: { text: string }) {
  const lines = text.split('\n').filter(Boolean);
  const keys = stableKeys(lines, (l) => l);
  return (
    <>
      {lines.map((line, i) => {
        const m = LOG_LINE.exec(line);
        const time = m ? m[2] : '';
        const msg = m ? m[3] : line;
        let cls = 'jl-msg';
        if (msg.startsWith('==== ')) cls += ' jl-start-text';
        else if (/\b(fail|failed|error)\b/i.test(msg)) cls += ' jl-err';
        else if (/\bok\b|ready|encoded with/i.test(msg)) cls += ' jl-ok';
        const isCmd = msg.startsWith('$ ');
        return (
          <div className={`jl-row${msg.startsWith('==== ') ? ' jl-start' : ''}`} key={keys[i]}>
            <span className="jl-time">{time}</span>
            {isCmd ? (
              <span className="jl-msg jl-cmd">
                {splitCommand(msg).map((part, j) => (
                  <span key={j} className={j ? 'jl-arg' : undefined}>
                    {part}
                  </span>
                ))}
              </span>
            ) : (
              <span className={cls}>{msg.replace(/^==== /, '').replace(/ ====$/, '')}</span>
            )}
          </div>
        );
      })}
    </>
  );
}
